import { Validation } from "@/backend/models/validation";
import { useParameterStore } from "@/frontend/store/ParameterStore";
import { HelpIcon } from "@/frontend/SVGs/HelpIcon";
import { parseReferences } from "@/frontend/utils/parseReferences";
import { I18n } from "@/i18n/I18n";
import { ParameterType, ParameterSubjectType } from "@/types/parameterTypes";
import { OPERAND_TYPE } from "@/types/validationTypes";
import { useState } from "react";
import { OverlayTrigger, Popover, Dropdown } from "react-bootstrap";
import { useShallow } from "zustand/react/shallow";
import { CollapseButton } from "../buttons/CollapseButton";
import { CustomToggle } from "../dropdown/CustomToggle";
import { DeleteItem } from "../dropdown/DeleteItem";
import { MonacoPythonEditor } from "../MonacoPythonEditor";
import { ParameterTable } from "../parameters/ParameterTable";
import { Form } from "react-bootstrap";

export function ContainerValidations({ subjectId }: { subjectId: string }) {
  const validations = useParameterStore(useShallow(state => state.validations.filter((v: Validation) => v.subjectId === subjectId)));
  const createContainerValidation = useParameterStore(state => state.createContainerValidation);
  
  return (
    <div className="mb-2">
      {validations.map(validation => (
        <ContainerValidation key={validation.id} validation={validation} />
      ))}
      <button type='button' className='btn btn-sm btn-primary' onClick={() => void createContainerValidation(subjectId)}>{I18n.get('plusValidation')}</button> 
    </div>
  );
}

function ContainerValidation(props: { validation: Validation }) {
  const { validation } = props;
  const parameters = useParameterStore(useShallow(state => state.parameters.filter(param => param.subjectId === validation.id)));
  const updateValidation = useParameterStore(state => state.updateValidation);
  const deleteValidation = useParameterStore(state => state.deleteValidation);
  const [expanded, setExpanded] = useState(true);
  const [failMessage, setFailMessage] = useState(validation.failMessage);
  const [failMessageError, setFailMessageError] = useState('');
  const [passMessage, setPassMessage] = useState(validation.passMessage);
  const [passMessageError, setPassMessageError] = useState('');
  const [title, setTitle] = useState(validation.title);

  const onChangeFailMessage = (value: string) => {
    setFailMessage(value);
    setFailMessageError('');
    const parseRefsError = parseReferences(value, parameters);
    if (parseRefsError) {
      setFailMessageError(parseRefsError);
    }
  };

  const onChangePassMessage = (value: string) => {
    setPassMessage(value);
    setPassMessageError('');
    const parseRefsError = parseReferences(value, parameters);
    if (parseRefsError) {
      setPassMessageError(parseRefsError);
    }
  };

  return (
    <div className='card card-body bg-light mb-2'>
      <div className='d-flex justify-content-between w-100'>
        <div className='d-flex align-items-center'>
          <CollapseButton showElement={expanded} setShowElement={setExpanded} title={I18n.get('stepValidation')} />
          <small className='text-muted text-uppercase'>{I18n.get('stepValidation')}</small>
          <OverlayTrigger
            trigger="click"
            placement="top"
            rootClose
            overlay={<Popover id="step-validation-help-popover">
              <Popover.Body>
                {I18n.get('stepValidationHelpText')}: <code>{`v.set_output("${OPERAND_TYPE.RESULT}", result)`}</code>
              </Popover.Body>
            </Popover>}
          >
            <button type='button' className='btn btn-sm' aria-label={I18n.get('stepValidationHelp')}><HelpIcon /></button>
          </OverlayTrigger>
        </div>
        <Dropdown drop='down'>
          <Dropdown.Toggle size='sm' as={CustomToggle(I18n.get('validation'))} variant='dark' id="dropdown-basic" />
          <Dropdown.Menu>
            <DeleteItem deleteAction={() => deleteValidation(validation.id)} itemType={I18n.get('validation')} title='' />
          </Dropdown.Menu>
        </Dropdown>
      </div>
      {expanded && (
        <>
          <ParameterTable subjectId={validation.id} parameterType={ParameterType.INPUT} subjectType={ParameterSubjectType.VALIDATION} />
          <div className='py-3'>
            <MonacoPythonEditor script={validation.script} onChange={(value) => void updateValidation({ validationParams: { ...validation, script: value }})} />
          </div>
          <Form.Label className="col-form-label col-form-label-sm">{I18n.get('title')}</Form.Label>
          <Form.Control
            aria-label={I18n.get('title')}
            defaultValue={title}
            onChange={(event) => setTitle(event.target.value)}
            onBlur={() => void updateValidation({ validationParams: { ...validation, title } })}
          />
          <Form.Label className="col-form-label col-form-label-sm">{I18n.get('failMessage')}</Form.Label>
          <Form.Control
            as="textarea"
            aria-label={I18n.get('failMessage')}
            defaultValue={failMessage}
            onChange={(event) => onChangeFailMessage(event.target.value)}
            onBlur={() => void updateValidation({ validationParams: { ...validation, failMessage } })}
            isInvalid={!!failMessageError}
          />
          <Form.Control.Feedback type="invalid">
            { failMessageError }
          </Form.Control.Feedback>
          <Form.Label className="col-form-label col-form-label-sm">{I18n.get('passMessage')}</Form.Label>
          <Form.Control
            as="textarea"
            aria-label={I18n.get('passMessage')}
            defaultValue={passMessage}
            onChange={(event) => onChangePassMessage(event.target.value)}
            onBlur={() => void updateValidation({ validationParams: { ...validation, passMessage } })}
            isInvalid={!!passMessageError}
          />
          <Form.Control.Feedback type="invalid">
            { passMessageError }
          </Form.Control.Feedback>
        </>
      )}
    </div>
  );
}
